import {
  createAsyncThunk,
  createEntityAdapter,
  createSelector,
  createSlice,
  PayloadAction,
} from '@reduxjs/toolkit';
import {
  Dashboard,
  DashboardAffordancesType,
  DashboardWithRelationships,
  NewDashboard,
  SortingType,
} from 'utils/customTypes';
import {
  DashboardSubPath,
  UpdateParams,
} from 'api/normalized/dashboard/dashboardAPI';
import { RootState, ThunkApi } from 'state/store';
import { Status } from 'utils/customTypes';
import { SLICE_STATUS } from 'utils/constants';
import { DASHBOARDS_LIST_ACTIONS } from 'utils/constants/dashboard';

/* ============================== STATE SETUP =============================== */

interface DashboardState {
  affordances: DashboardAffordancesType;
  collectiveStatus: Status;
  byIdStatus: Record<string, Status>;
  sort: {
    orderBy: string;
    sortingType: SortingType;
  };
  currentDashboardId?: string;
}

const initialState: DashboardState = {
  affordances: {
    global: {},
    resources: {},
  },
  collectiveStatus: SLICE_STATUS.IDLE,
  byIdStatus: {},
  sort: {
    orderBy: 'createdAt',
    sortingType: 'desc',
  },
};

const dashboardAdapter = createEntityAdapter<Dashboard>();

/* ============================== REDUX THUNK =============================== */
function listConcurrencyCondition(
  thunkParameters: unknown,
  { getState }: { getState: () => RootState }
): boolean {
  const { collectiveStatus, byIdStatus } = getState().normalized.dashboard;

  const isCollectiveBusy =
    collectiveStatus === SLICE_STATUS.LOADING ||
    collectiveStatus === SLICE_STATUS.UPDATING;

  const isByIdBusy = Object.values(byIdStatus).some(
    (status) =>
      status === SLICE_STATUS.LOADING || status === SLICE_STATUS.UPDATING
  );

  return !isCollectiveBusy && !isByIdBusy;
}

function byIdConcurrencyCondition(
  params: { dashboardId: string },
  { getState }: { getState: () => RootState }
): boolean {
  const { collectiveStatus, byIdStatus } = getState().normalized.dashboard;

  const isCollectiveBusy =
    collectiveStatus === SLICE_STATUS.LOADING ||
    collectiveStatus === SLICE_STATUS.UPDATING;

  const isThisIdBusy =
    byIdStatus[params.dashboardId] === SLICE_STATUS.LOADING ||
    byIdStatus[params.dashboardId] === SLICE_STATUS.UPDATING;

  return !isCollectiveBusy && !isThisIdBusy;
}

export const getDashboards = createAsyncThunk<
  {
    data: Dashboard[];
    affordances: DashboardAffordancesType;
  },
  void,
  ThunkApi
>(
  'normalized/dashboard/getDashboards',
  async (_, { extra: { getDashboardApi } }) => {
    return await getDashboardApi().getList();
  },
  { condition: listConcurrencyCondition }
);

export const getDashboardById = createAsyncThunk<
  Dashboard,
  { dashboardId: string },
  ThunkApi
>(
  'normalized/dashboard/getDashboardById',
  async ({ dashboardId }, { extra: { getDashboardApi } }) => {
    return await getDashboardApi().getById(dashboardId);
  },
  { condition: byIdConcurrencyCondition }
);

export const getDashboardByIdWithRelationships = createAsyncThunk<
  DashboardWithRelationships,
  { dashboardId: string; relationship: DashboardSubPath },
  ThunkApi
>(
  'normalized/dashboard/getDashboardByIdWithIncludes',
  async ({ dashboardId, relationship }, { extra: { getDashboardApi } }) => {
    return await getDashboardApi().getByIdWithRelationships(
      dashboardId,
      relationship
    );
  },
  { condition: byIdConcurrencyCondition }
);

export const getDashboardByIdWithAllRelationships = createAsyncThunk<
  DashboardWithRelationships,
  { dashboardId: string },
  ThunkApi
>(
  'normalized/dashboard/getDashboardByIdWithAllRelationships',
  async ({ dashboardId }, { extra: { getDashboardApi } }) => {
    return await getDashboardApi().getByIdWithAllRelationships(dashboardId);
  },
  { condition: byIdConcurrencyCondition }
);

export const createDashboard = createAsyncThunk<
  DashboardWithRelationships,
  { prototype: NewDashboard },
  ThunkApi
>(
  'normalized/dashboard/create',
  async ({ prototype }, { getState, extra: { getDashboardApi } }) => {
    const globalAffordances =
      getState().normalized.dashboard.affordances.global;
    const createUrl = globalAffordances?.[DASHBOARDS_LIST_ACTIONS.CREATE] ?? '';
    return await getDashboardApi().createDashboard(createUrl, prototype);
  }
);

export const createDashboardThroughDuplication = createAsyncThunk<
  DashboardWithRelationships,
  { duplicationDashboardId: string; prototype: NewDashboard },
  ThunkApi
>(
  'normalized/dashboard/createThroughDuplication',
  async (
    { duplicationDashboardId, prototype },
    { extra: { getDashboardApi } }
  ) => {
    return await getDashboardApi().createThroughDuplication(
      duplicationDashboardId,
      prototype
    );
  }
);

function createUpdateDashboardThunk<T extends Partial<Dashboard>>() {
  return createAsyncThunk<
    { dashboardId: string; changes: Partial<Dashboard> },
    { updateParams: UpdateParams<T> },
    ThunkApi
  >(
    'normalized/dashboard/updateDashboard',
    async ({ updateParams }, { getState, extra: { getDashboardApi } }) => {
      const { dashboardId } = updateParams;

      const affordances =
        getState().normalized.dashboard.affordances.resources[dashboardId];
      const updateUrl = affordances?.[DASHBOARDS_LIST_ACTIONS.EDIT] ?? '';
      const changes = await getDashboardApi().updateDashboard(
        updateUrl,
        updateParams
      );
      return { dashboardId, changes };
    },
    {
      condition: (arg, state) =>
        byIdConcurrencyCondition(
          { dashboardId: arg.updateParams.dashboardId },
          state
        ),
    }
  );
}

export const updateName = createUpdateDashboardThunk<{ name: string }>();
const updateThunks = [updateName];

export const deleteDashboard = createAsyncThunk<string, string, ThunkApi>(
  'normalized/dashboard/deleteDashboard',
  async (dashboardId, { getState, extra: { getDashboardApi } }) => {
    const affordances =
      getState().normalized.dashboard.affordances.resources[dashboardId];
    const deleteUrl = affordances?.[DASHBOARDS_LIST_ACTIONS.DELETE] ?? '';
    await getDashboardApi().deleteDashboard(deleteUrl);
    return dashboardId;
  },
  {
    condition: (arg, state) =>
      byIdConcurrencyCondition({ dashboardId: arg }, state),
  }
);

/* ================================= REDUCER ================================ */
const dashboardSlice = createSlice({
  name: 'normalized/dashboards',
  initialState: dashboardAdapter.getInitialState(initialState),
  reducers: {
    setCurrentDashboardId: (state, action: PayloadAction<string>) => {
      state.currentDashboardId = action.payload;
    },
    resetCurrentDashboard: (state) => {
      state.currentDashboardId = undefined;
    },
    resetDashboardByIdStatus: (state, action: PayloadAction<string>) => {
      delete state.byIdStatus[action.payload];
    },
    setSort: (
      state,
      action: PayloadAction<{ orderBy: string; sortingType: SortingType }>
    ) => {
      state.sort = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getDashboards.pending, (state) => {
        state.collectiveStatus = SLICE_STATUS.LOADING;
      })
      .addCase(getDashboards.rejected, (state) => {
        state.collectiveStatus = SLICE_STATUS.FAILED;
      })
      .addCase(getDashboards.fulfilled, (state, action) => {
        dashboardAdapter.upsertMany(state, action.payload.data);
        state.ids = Array.from(
          new Set([
            ...state.ids,
            ...action.payload.data.map((dashboard) => dashboard.id),
          ])
        );
        Object.assign(state.affordances, action.payload.affordances);
        state.collectiveStatus = SLICE_STATUS.SUCCESS;
      })
      .addCase(getDashboardById.pending, (state, action) => {
        const id = action.meta.arg.dashboardId;
        state.byIdStatus[id] = SLICE_STATUS.LOADING;
      })
      .addCase(getDashboardById.rejected, (state, action) => {
        const id = action.meta.arg.dashboardId;
        state.byIdStatus[id] = SLICE_STATUS.FAILED;
      })
      .addCase(getDashboardById.fulfilled, (state, action) => {
        dashboardAdapter.upsertOne(state, action.payload);
        const id = action.meta.arg.dashboardId;
        state.byIdStatus[id] = SLICE_STATUS.SUCCESS;
      })
      .addCase(getDashboardByIdWithRelationships.pending, (state, action) => {
        const id = action.meta.arg.dashboardId;
        state.byIdStatus[id] = SLICE_STATUS.LOADING;
      })
      .addCase(getDashboardByIdWithRelationships.rejected, (state, action) => {
        const id = action.meta.arg.dashboardId;
        state.byIdStatus[id] = SLICE_STATUS.FAILED;
      })
      .addCase(getDashboardByIdWithRelationships.fulfilled, (state, action) => {
        dashboardAdapter.upsertOne(state, action.payload.dashboard);
        const id = action.meta.arg.dashboardId;
        if (action.payload.resourceAffordances) {
          state.affordances.resources[id] = action.payload.resourceAffordances;
        }
        state.byIdStatus[id] = SLICE_STATUS.SUCCESS;
      })
      .addCase(
        getDashboardByIdWithAllRelationships.pending,
        (state, action) => {
          const id = action.meta.arg.dashboardId;
          state.byIdStatus[id] = SLICE_STATUS.LOADING;
        }
      )
      .addCase(
        getDashboardByIdWithAllRelationships.rejected,
        (state, action) => {
          const id = action.meta.arg.dashboardId;
          state.byIdStatus[id] = SLICE_STATUS.FAILED;
        }
      )
      .addCase(
        getDashboardByIdWithAllRelationships.fulfilled,
        (state, action) => {
          dashboardAdapter.upsertOne(state, action.payload.dashboard);
          const id = action.meta.arg.dashboardId;
          if (action.payload.resourceAffordances) {
            state.affordances.resources[id] =
              action.payload.resourceAffordances;
          }
          state.byIdStatus[id] = SLICE_STATUS.SUCCESS;
        }
      )
      .addCase(createDashboardThroughDuplication.pending, (state, _) => {
        state.collectiveStatus = SLICE_STATUS.LOADING;
      })
      .addCase(createDashboardThroughDuplication.rejected, (state, _) => {
        state.collectiveStatus = SLICE_STATUS.FAILED;
      })
      .addCase(createDashboardThroughDuplication.fulfilled, (state, action) => {
        dashboardAdapter.addOne(state, action.payload.dashboard);
        state.collectiveStatus = SLICE_STATUS.SUCCESS;
      })
      .addCase(createDashboard.pending, (state, _) => {
        state.collectiveStatus = SLICE_STATUS.LOADING;
      })
      .addCase(createDashboard.rejected, (state, _) => {
        state.collectiveStatus = SLICE_STATUS.FAILED;
      })
      .addCase(createDashboard.fulfilled, (state, action) => {
        dashboardAdapter.addOne(state, action.payload.dashboard);
        state.collectiveStatus = SLICE_STATUS.SUCCESS;
      })
      .addCase(deleteDashboard.pending, (state, action) => {
        const id = action.meta.arg;
        state.byIdStatus[id] = SLICE_STATUS.LOADING;
      })
      .addCase(deleteDashboard.rejected, (state, action) => {
        const id = action.meta.arg;
        state.byIdStatus[id] = SLICE_STATUS.FAILED;
      })
      .addCase(deleteDashboard.fulfilled, (state, action) => {
        const dashboardId = action.payload;
        dashboardAdapter.removeOne(state, dashboardId);
        delete state.affordances.resources[dashboardId];
        state.byIdStatus[dashboardId] = SLICE_STATUS.SUCCESS;
      })
      .addMatcher(
        (action): action is ReturnType<typeof updateName.pending> =>
          updateThunks.some((thunk) => thunk.pending.match(action)),
        (state, action) => {
          const id = action.meta.arg.updateParams.dashboardId;
          state.byIdStatus[id] = SLICE_STATUS.LOADING;
        }
      )
      .addMatcher(
        (action): action is ReturnType<typeof updateName.rejected> =>
          updateThunks.some((thunk) => thunk.rejected.match(action)),
        (state, action) => {
          const id = action.meta.arg.updateParams.dashboardId;
          state.byIdStatus[id] = SLICE_STATUS.FAILED;
        }
      )
      .addMatcher(
        (action): action is ReturnType<typeof updateName.fulfilled> =>
          updateThunks.some((thunk) => thunk.fulfilled.match(action)),
        (state, action) => {
          const { dashboardId, changes } = action.payload;
          dashboardAdapter.updateOne(state, { id: dashboardId, changes });
          state.byIdStatus[dashboardId] = SLICE_STATUS.SUCCESS;
        }
      );
  },
});

/* ================================ ACTIONS ================================= */
export const {
  resetCurrentDashboard,
  resetDashboardByIdStatus,
  setCurrentDashboardId,
  setSort,
} = dashboardSlice.actions;

/* =============================== SELECTORS ================================ */
const defaultSelectors = dashboardAdapter.getSelectors<RootState>(
  (state) => state.normalized.dashboard
);

const selectNumberOfLoadedDashboards = (state: RootState) =>
  state.normalized.dashboard.ids.length;

const selectDashboardById = (state: RootState, id: string) =>
  dashboardSelectors.selectById(state, id);

const selectCurrentDashboardId = (state: RootState) =>
  state.normalized.dashboard.currentDashboardId;

const selectCurrentDashboard = (state: RootState) => {
  const currentDashboardId = state.normalized.dashboard.currentDashboardId;
  if (!currentDashboardId) {
    return undefined;
  }
  return selectDashboardById(state, currentDashboardId);
};

const selectSort = (state: RootState) => state.normalized.dashboard.sort;

const selectDashboardStatusByDashboardId = (
  state: RootState,
  dashboardId: string
) => state.normalized.dashboard.byIdStatus[dashboardId];

const selectAffordancesByDashboardId = (
  state: RootState,
  dashboardId: string
): Record<string, string> | undefined =>
  state.normalized.dashboard.affordances.resources[dashboardId];

const selectAllDashboardAffordances = (state: RootState) =>
  state.normalized.dashboard.affordances.resources;

const selectGlobalAffordances = (state: RootState) =>
  state.normalized.dashboard.affordances.global;

const selectDashboardAffordanceFlags = createSelector(
  selectAffordancesByDashboardId,
  (affordances) => ({
    canEdit: Boolean(affordances?.[DASHBOARDS_LIST_ACTIONS.EDIT]),
    canDelete: Boolean(affordances?.[DASHBOARDS_LIST_ACTIONS.DELETE]),
    canShare: Boolean(affordances?.[DASHBOARDS_LIST_ACTIONS.SHARE]),
    canFilter: Boolean(affordances?.[DASHBOARDS_LIST_ACTIONS.FILTERS]),
  })
);

const selectGlobalAffordanceFlags = createSelector(
  selectGlobalAffordances,
  (affordances) => ({
    canCreate: Boolean(affordances?.[DASHBOARDS_LIST_ACTIONS.CREATE]),
  })
);

export const dashboardSelectors = {
  ...defaultSelectors,
  selectNumberOfLoadedDashboards,
  selectDashboardById,
  selectCurrentDashboard,
  selectCurrentDashboardId,
  selectSort,
  selectDashboardStatusByDashboardId,
  selectAffordancesByDashboardId,
  selectAllDashboardAffordances,
  selectGlobalAffordances,
  selectDashboardAffordanceFlags,
  selectGlobalAffordanceFlags,
};

export default dashboardSlice.reducer;
